import React from 'react';
import { fontWeightMediumLarge } from 'utils/fontWeightConstants';
import { makeStyles } from 'vendor/material';
import cn from 'classnames';

const useStyles = makeStyles((theme) => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
      gridTemplateColumns: '1fr 2fr 2fr',
    },
    paddingBlock: theme.spacing(2),
    gap: theme.spacing(2),
  },
  listItem: {
    borderBottom: `1px solid ${theme.palette.NEUTRAL030}`,
  },
  title: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: fontWeightMediumLarge,
    lineHeight: theme.typography.pxToRem(20),
    color: theme.palette.NEUTRAL300,
    margin: 0,
  },
  detail: {
    color: theme.palette.NEUTRAL800,
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(24),
  },
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  name: {
    color: theme.palette.NEUTRAL500,
    wordBreak: 'break-word',
  },
}));

/**
 * MemberList styled component with additional subcomponents for layout structure.
 */
interface MemberListComponent extends React.FC<{ children: React.ReactNode }> {
  /**
   *  Grid header with column titles
   */
  Header: React.FC<{ columnHeaders: string[] }>;
  /**
   * Grid row with for member details
   */
  Item: React.FC<{
    name: string;
    email: string;
    phoneNumber: string;
  }>;
}

/**
 * Responsive grid layout for displaying member information with header and items.
 * @component
 * @example
 * <MemberList>
 *   <MemberList.Header columnHeaders={['Name', 'Email', 'Phone']} />
 *   <MemberList.Item
 *     name="John Doe"
 *     email="john@example.com"
 *     phoneNumber="555-0123"
 *   />
 * </MemberList>
 */
const MemberList: MemberListComponent = ({ children }) => <div>{children}</div>;

const ListHeader: MemberListComponent['Header'] = ({ columnHeaders }) => {
  const classes = useStyles();
  return (
    <div className={cn(classes.grid)}>
      {columnHeaders.map((header) => (
        <div key={header} className={classes.title}>
          {header}
        </div>
      ))}
    </div>
  );
};

const ListItem: MemberListComponent['Item'] = ({
  email,
  name,
  phoneNumber,
}) => {
  const classes = useStyles();
  return (
    <div className={cn([classes.grid, classes.detail, classes.listItem])}>
      <div className={classes.name}>{name}</div>
      <div className={classes.ellipsis}>{email ?? '-'}</div>
      <div className={classes.ellipsis}>{phoneNumber ?? '-'}</div>
    </div>
  );
};

MemberList.Header = ListHeader;
MemberList.Item = ListItem;

export default MemberList;
